import React, { FC, useRef, useState } from 'react';
import Accordion from '~ComponentsImproved/Accordion';
import Icon from '~ComponentsImproved/Icon';
import { BlueButton, Box, Button } from '~Styles';
import { DateTime } from 'luxon';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useBridgeApi, useButton, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import DataAlertIcon from '~Assets/icons/data_alert.svg';
import * as actions from '~actions';
import QuestionModal from '~ComponentsImproved/QuestionModal';
import SyncIcon from '~Assets/icons/sync.svg';

interface Props {
  type: 'orderStatusEvents' | 'tableEvents';
  eventsQuantity: number;
  onIgnore?: (type: 'orderStatusEvents' | 'tableEvents') => void;
}

/** Show this with buttons to prompt reload the app due to high cache volume in either orderStatusEvents or tableEvents */
const HighEventVolume: FC<Props> = ({ type, eventsQuantity, onIgnore }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const logger = useLogger('system-status');
  const api = useBridgeApi();

  const posPayments = useSelector((state) => state.POSPayments);

  // Use useRef to store the initial render time that persists across re-renders
  const initialRenderTimeRef = useRef<Date>(new Date());

  const [showReloadModal, setShowReloadModal] = useState<boolean>(false);
  const [ignoreWarning, setIgnoreWarning] = useState<boolean>(false);

  const reloadApp = () => {
    if (posPayments.length > 0) {
      logger.info('Reload not allowed: Ongoing payments');
      dispatch(
        actions.addToast(i18n('Menu.PoweredBy.Reload.NotAllowed'), true, 'ongoing-payment', true),
      );
      return;
    }

    logger.warn(
      `HighEventVolume: Reloading app due to high event volume (event quantity: ${eventsQuantity}) for ${type}. Initial render time for this warning: ${initialRenderTimeRef.current.toISOString()}`,
    );
    setTimeout(() => {
      api.reload();
    }, 100);
  };

  const dismissButton = useButton({
    onClick: () => {
      setIgnoreWarning(true);
      onIgnore?.(type);
    },
  });

  const reloadButton = useButton({
    onClick: () => {
      setShowReloadModal(true);
    },
  });

  if (ignoreWarning) {
    return null;
  }

  return (
    <>
      <Accordion
        title={
          <Box display="flex" alignItems="center" gap="xs">
            <Icon src={DataAlertIcon} height="32px" width="32px" color="var(--colorUIYellow)" />
            <Box display="flex" flexDirection="column">
              <Paragraph level={5}>
                {DateTime.fromJSDate(initialRenderTimeRef.current).toFormat('HH:mm')}
              </Paragraph>
              <Heading level={4}>
                {type === 'orderStatusEvents'
                  ? i18n('SystemStatus.Warning.OrderStatus')
                  : i18n('SystemStatus.Warning.TableStatus')}
              </Heading>
            </Box>
          </Box>
        }
        dark={false}>
        <Box display="flex" flexDirection="column" gap="xs">
          <Heading level={4}>{i18n('SystemStatus.Warning.Heading')}</Heading>

          <Paragraph level={4}>{i18n('SystemStatus.Warning.Desc')}</Paragraph>

          <Paragraph level={5} color="var(--colorBlueGray1200)">
            {i18n('SystemStatus.Warning.NrOfEvents')}: {eventsQuantity}
          </Paragraph>
        </Box>

        <Box display="flex" gap="xs" justifyContent="flex-end" marginTop="small">
          <Button {...dismissButton}>{i18n('PrinterStatus.Errors.Dismiss')}</Button>
          <BlueButton {...reloadButton}>{i18n('KDS.Reload')}</BlueButton>
        </Box>
      </Accordion>

      {showReloadModal && (
        <QuestionModal
          question={i18n('Menu.PoweredBy.ReloadQuestion')}
          isShowing={showReloadModal}
          onLeftClick={() => setShowReloadModal(false)}
          onRightClick={reloadApp}
          backdropClose={() => setShowReloadModal(false)}
          icon={SyncIcon}
        />
      )}
    </>
  );
};

export default React.memo(HighEventVolume);
